import type { Spritesheet, Texture } from "pixi.js";
import type { SkeletonData, TextureAtlas } from "@esotericsoftware/spine-pixi-v8";
import { SpriteFontDefinition, createSkeletonData } from "@slot/presentation";
import type { Log, SpriteFontConfigJson } from "@slot/presentation";
import type { BootLaunchPayload, BrandedAssetResolver } from "@slot/game-shell";

/** Each Spine skeleton ships beside its own atlas: the skeleton JSON's path and its `.atlas`. */
const SKELETONS = {
  background: "backgrounds/spine/background/json/background",
  bear: "character/bear_character/json/bear",
  vsWild: "expanding-wild/vs/spine/json/horizontal",
  anticipationFrame: "anticipation/assets/skeleton",
} as const;

/**
 * Everything the round's visuals need, resolved ONCE from the loaded bundles through the branded resolver (operator
 * bundle first for allowlisted paths). Scene builders, views and features take this bag; nothing else touches the
 * asset registry. Missing optional art resolves to null and is logged, so the game still boots without it. The symbols
 * sheet is the one required entry: `getAssetOrThrow` throws when it is missing, uncaught, and the boot boundary shows
 * the boot-failure popup.
 */
export interface GameVisualAssets {
  readonly symbols: Spritesheet;
  readonly fonts: {
    /** The grid's win-amount label digits. */
    readonly woodenNumbers: SpriteFontDefinition | null;
    /** The big win's amount digits. */
    readonly grave: SpriteFontDefinition | null;
  };
  readonly skeletons: {
    /** The animated scene background (also the feature trigger's cover). */
    readonly background: SkeletonData | null;
    /** The bear character. */
    readonly bear: SkeletonData | null;
    /** The VS battle (`boy` / `bear` clips). */
    readonly vsWild: SkeletonData | null;
    /** The anticipation frame over a teased reel. */
    readonly anticipationFrame: SkeletonData | null;
  };
  readonly textures: {
    readonly baseGridFrame: Texture | null;
    readonly featureGridFrame: Texture | null;
    /** The static (brandable) background, when the Spine one is missing. */
    readonly background: Texture | null;
    /** The Super Bonus saved-total panel's plate. */
    readonly featureMultiplierPlate: Texture | null;
    /** The big win's blood splatter. */
    readonly bloodSplatter: Texture | null;
    /** The feature-finished cover (the boot loader's background art). */
    readonly featureFinishedCover: Texture | null;
    /** The shell's soft glow (the big win's red glow behind its number). */
    readonly glow: Texture | null;
  };
  readonly sheets: {
    /** The payline needle: `start`, `mid`, `end`. */
    readonly needleLine: Spritesheet | null;
    /** The big win's titles (`big`, `huge`, `mega`, `win`) and its `chainsaw`. */
    readonly bigWinTitles: Spritesheet | null;
    /** The VS battle frame's art. */
    readonly vsWildFrame: Spritesheet | null;
    readonly vsWildArt: Spritesheet | null;
  };
}

export function loadGameVisualAssets(input: { payload: BootLaunchPayload; log: Log }): GameVisualAssets {
  const { payload, log } = input;
  const branded: BrandedAssetResolver = payload.brandedAssets;
  const optional = <T>(path: string, what: string): T | null => {
    const asset = branded.getAssetOrNull<T>(path);
    if (asset === null) log(`[GameVisualAssets] ${what} "${path}" not loaded`);
    return asset;
  };
  // The shell's art is not brandable: read from `shell-ui` directly, logged on a miss like the rest.
  const shellUiTexture = (path: string): Texture | null => {
    const texture = branded.getFromBundleOrNull<Texture>("shell-ui", path);
    if (texture === null) log(`[GameVisualAssets] shell-ui texture "${path}" not loaded`);
    return texture;
  };
  const skeleton = (path: string): SkeletonData | null => {
    const json = branded.getAssetOrNull<unknown>(path);
    const atlas = branded.getAssetOrNull<TextureAtlas>(`${path}.atlas`);
    if (!json || !atlas) {
      log(`[GameVisualAssets] skeleton "${path}" not loaded (json=${!!json} atlas=${!!atlas})`);
      return null;
    }
    try {
      return createSkeletonData({ skeleton: json, atlas });
    } catch (error) {
      log(`[GameVisualAssets] skeleton "${path}" failed to parse: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  };

  return {
    symbols: branded.getAssetOrThrow<Spritesheet>("symbols/atlases/symbols"),
    fonts: {
      woodenNumbers: loadSpriteFont(branded, "wooden-numbers", log),
      grave: loadSpriteFont(branded, "grave", log),
    },
    skeletons: {
      background: skeleton(SKELETONS.background),
      bear: skeleton(SKELETONS.bear),
      vsWild: skeleton(SKELETONS.vsWild),
      anticipationFrame: skeleton(SKELETONS.anticipationFrame),
    },
    textures: {
      baseGridFrame: optional<Texture>("backgrounds/BaseGridFrame", "texture"),
      featureGridFrame: optional<Texture>("backgrounds/FeatureGridFrame", "texture"),
      background: optional<Texture>("backgrounds/background", "texture"),
      featureMultiplierPlate: optional<Texture>("backgrounds/feature_mult_bg", "texture"),
      bloodSplatter: optional<Texture>("backgrounds/blood_splatter", "texture"),
      featureFinishedCover: branded.getFromBundleOrNull<Texture>("boot-assets", "background-intro"),
      glow: shellUiTexture("sprites/essentials/glow"),
    },
    sheets: {
      needleLine: optional<Spritesheet>("symbols/atlases/needle-line", "spritesheet"),
      bigWinTitles: optional<Spritesheet>("big-win-titles/bigWinSprite", "spritesheet"),
      vsWildFrame: optional<Spritesheet>("expanding-wild/vs/expanded-wild-vs", "spritesheet"),
      vsWildArt: optional<Spritesheet>("expanding-wild/vs/expanding-vs-wild-2", "spritesheet"),
    },
  };
}

function loadSpriteFont(branded: BrandedAssetResolver, name: string, log: Log): SpriteFontDefinition | null {
  const sheet = branded.getAssetOrNull<Spritesheet>(`fonts/atlases/${name}`);
  const config = branded.getAssetOrNull<SpriteFontConfigJson>(`fonts/configs/${name}`);
  if (!sheet || !config) {
    log(`[GameVisualAssets] sprite font "${name}" not loaded (sheet=${!!sheet} config=${!!config})`);
    return null;
  }
  return SpriteFontDefinition.fromConfig({ config, atlas: sheet.textures, log });
}
